import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import { SMS_ACTIVITIES_PER_PAGE } from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import BaseButton from 'components/atoms/baseButton';
import { MAX_TEXT_LENGTH_FOR_EXPAND } from 'infrastructure/consts/tables';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import { useModuleWithLocalApcm } from 'infrastructure/providers/module-provider';

import s from './styles.module.scss';
import { columns } from './columns';
import { useClinicNotes } from './hooks/use-clinic-notes';
import ModuleTabs from '../module-tabs';
import ClinicalNotesLoggedModal from '../clinical-notes-logged-modal';

import type { DateRange, SortState } from 'infrastructure/types';
import type { IBaseTab } from 'components/atoms/base-tabs';
import type { ColumnsProps } from './columns';
import type { IClinicNotes } from 'infrastructure/interfaces';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';

interface IProps {
  patientGuid: string;
  dateRange?: DateRange | [];
  showTabs?: boolean;
  module?: ModuleType;
  allowAdd?: boolean;
}

interface HeaderSectionProps {
  title: string;
  btnLabel: string;
  disabledAdd: boolean;
  showTabs: boolean;
  module?: ModuleType;
  onAddNote: () => void;
  onModuleChange: (tab: IBaseTab<ModuleType>) => void;
  allowAdd?: boolean;
}

const HeaderSection: FC<HeaderSectionProps> = ({
  title,
  btnLabel,
  disabledAdd,
  showTabs,
  module,
  onAddNote,
  onModuleChange,
  allowAdd,
}) => (
  <div className={s['title-wrapper']}>
    <h3 className={s.title} data-cy="clinical-notes-section">
      {title}
    </h3>
    {allowAdd && (
      <BaseButton
        label={`+ ${btnLabel}`}
        dataCy="addNote-button"
        disabled={disabledAdd}
        onClick={onAddNote}
      />
    )}
    {showTabs && (
      <ModuleTabs
        activeKey={module}
        className={s.tabs}
        onChange={onModuleChange}
        showApcm
      />
    )}
  </div>
);

interface TableSectionProps {
  clinicalNotes: IClinicNotes[];
  loading: boolean;
  page: number;
  totalCount: number;
  expandedRecord?: ColumnsProps['expandedRecord'];
  onPageChange: (page: number) => void;
  onExpand: (expanded: boolean, record: IClinicNotes) => void;
  emptyText: string;
  deleteNote: TDeleteCommentFn;
  setDocumentationTimeSort: (sort: SortState) => void;
  module?: ModuleType;
}

const TableSection: FC<TableSectionProps> = ({
  clinicalNotes,
  loading,
  page,
  totalCount,
  expandedRecord,
  onPageChange,
  onExpand,
  emptyText,
  deleteNote,
  setDocumentationTimeSort,
  module,
}) => (
  <BaseTableNew<IClinicNotes>
    items={clinicalNotes}
    columns={columns({
      expandedRecord,
      deleteNote,
      module,
    })}
    bordered
    loading={loading}
    pagination={{
      current: page,
      pageSize: SMS_ACTIVITIES_PER_PAGE,
      total: totalCount,
      position: ['bottomCenter'],
    }}
    onChangePage={onPageChange}
    dataCy="clinic-notes-table"
    expandedRowRender={(record) => (
      <div className={s.expandedRow}>{record.note}</div>
    )}
    rowExpandable={(record) => record.note.length >= MAX_TEXT_LENGTH_FOR_EXPAND}
    onExpand={onExpand}
    emptyText={<EmptyTablePlaceholder text={emptyText} />}
    onSort={(field, order) => {
      setDocumentationTimeSort(order);
    }}
  />
);

const ClinicalNotesLoggedTable: FC<IProps> = ({
  patientGuid,
  module = ModuleType.CCM,
  showTabs,
  dateRange = [],
  allowAdd = true,
}) => {
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const { module: moduleType, changeModule: setModuleType } =
    useModuleWithLocalApcm(module);

  const [expandedRecord, setExpandedRecord] =
    useState<ColumnsProps['expandedRecord']>();

  const {
    clinicalNotes,
    loading,
    disableButton,
    page,
    opened,
    toggleOpen,
    setPage,
    deleteNote,
  } = useClinicNotes({
    patientGuid,
    dateRange,
    module: moduleType,
    documentationTimeSort,
  });

  const { t } = useTranslation();
  const disabledAdd = disableButton || loading;

  const btnLabel = {
    [ModuleType.RPM]: t('labels.addModuleNote', { module: 'RPM' }),
    [ModuleType.CCM]: t('labels.addModuleNote', { module: 'CCM' }),
    [ModuleType.APCM]: t('labels.addModuleNote', { module: 'APCM' }),
    [ModuleType.ADMIN]: '',
    [ModuleType.RPMCCM]: '',
  } satisfies Record<ModuleType, string>;

  const handleModuleChange = (tab: IBaseTab<ModuleType>) => {
    setModuleType(tab.key);
    setPage(1);
  };

  return (
    <>
      <div className={s.wrapper}>
        <HeaderSection
          title={t('labels.clinicalNotesLogged')}
          btnLabel={btnLabel[moduleType]}
          disabledAdd={disabledAdd}
          showTabs={showTabs ?? false}
          onAddNote={toggleOpen}
          onModuleChange={handleModuleChange}
          module={moduleType}
          allowAdd={allowAdd}
        />
        <TableSection
          clinicalNotes={clinicalNotes?.items ?? []}
          loading={loading}
          page={page}
          totalCount={clinicalNotes?.count ?? 0}
          expandedRecord={expandedRecord}
          onPageChange={setPage}
          onExpand={(expanded, record) => {
            setExpandedRecord({ expanded, guid: record.guid });
          }}
          emptyText={t('labels.noClinicalNotesLogged')}
          deleteNote={(record) => deleteNote(record.guid)}
          setDocumentationTimeSort={setDocumentationTimeSort}
          module={moduleType}
        />
      </div>
      {opened && (
        <ClinicalNotesLoggedModal
          module={moduleType}
          patientGuid={patientGuid}
          open={opened}
          onClose={toggleOpen}
          isInitialTemplateUsed={clinicalNotes?.isInitialTemplateUsed}
        />
      )}
    </>
  );
};

export default ClinicalNotesLoggedTable;
